import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import { Link } from 'react-router-dom';

import picture from 'four-nets/icons/svg/icon_picture.svg';
import clock from 'four-nets/icons/svg/icon_activity2.svg';
import user from 'four-nets/icons/svg/icon_user.svg';
import tag from 'four-nets/icons/svg/icon_tag.svg';
import users from 'four-nets/icons/svg/icon_users.svg';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';
import ResponsiveImageView from 'lib/images/responsive-image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { ProductESFragment } from './product-es.graphql';
import defaultStyles from '../product-common.scss';

export interface Props extends ProductESFragment {
    styles: typeof defaultStyles;
}

const priceFormatter = Intl.NumberFormat();

const ProductES: React.FC<Props> = (props, context) => {
    const { site } = context;
    const {
        styles,
        productTitle,
        productDescription,
        userUsername,
        buyersCount,
        categoryName,
        categorySlug,
        url,
        price,
        lastUpdate,
        isVendor,
        featuredImage,
    } = props;

    const priceToShow = !price
        ? null
        : priceFormatter
              .formatToParts(price)
              .map(part => (part.type === 'group' ? { type: 'group', value: ' ' } : part))
              .map(part => part.value)
              .join('');

    return (
        <div className={classnames(styles.tile, { [styles.used]: !isVendor })}>
            <div className={classnames(styles.image, { [styles.used]: !isVendor })}>
                <Link to={url || ''}>
                    {featuredImage ? (
                        <FormattedMessage id='IMAGE'>
                            {msg => (
                                <ResponsiveImageView
                                    alt={`${productTitle} - ${msg} č. 1`}
                                    aspectFill={true}
                                    image={featuredImage}
                                    sizes={{ xs: '90vw', md: 250 }}
                                    linkify={false}
                                />
                            )}
                        </FormattedMessage>
                    ) : (
                        <div className={styles.noPhoto}>
                            <SVGIcon style={{ image: styles.icon }} src={picture} />
                        </div>
                    )}
                </Link>
            </div>
            <div className={styles.contentWrapper}>
                <div className={styles.dateAndBadgeDiv}>
                    <div className={styles.timeDiv}>
                        {lastUpdate ? (
                            <div className={styles.date}>
                                <SVGIcon style={{ image: styles.icon }} src={clock} />
                                <FormattedDateAgoView value={new Date(lastUpdate)} />
                            </div>
                        ) : null}
                    </div>
                    {site && !site.isGarden ? (
                        <div className={classnames(styles.newLabel, { [styles.usedLabel]: !isVendor })}>
                            {isVendor ? (
                                <FormattedMessage id='NEW_GOODS' />
                            ) : (
                                <FormattedMessage id='PRODUCTS_FROM_BAZAAR' />
                            )}
                        </div>
                    ) : (
                        <div className={classnames(styles.newLabel, { [styles.usedLabel]: !isVendor })}>
                            {isVendor ? (
                                <FormattedMessage id='NEW_GOODS' />
                            ) : (
                                <FormattedMessage id='PRODUCTS_FROM_SECOND_HAND' />
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.mainLinkWrapper}>
                    <Link to={url || ''} className={styles.mainLink}>
                        {productTitle}
                    </Link>
                </div>
                <div className={styles.sellingInfoDiv}>
                    <div className={styles.price}>
                        <div className={styles.priceContent}>
                            {price && price > 0 ? (
                                `${priceToShow} ${site && site.currency}`
                            ) : (
                                <FormattedMessage id='GIVE_FOR_FREE' />
                            )}
                        </div>
                    </div>
                    {buyersCount ? (
                        <div className={styles.buyersWrapper}>
                            <SVGIcon style={{ image: styles.icon }} src={users} />
                            <span className={styles.container}>{buyersCount}</span>
                        </div>
                    ) : (
                        <span />
                    )}
                </div>
            </div>
            <div className={styles.user}>
                <div className={styles.userInfoDiv}>
                    <div className={styles.userWrapper}>
                        <SVGIcon style={{ image: styles.userIcon }} src={user} />
                        <Link to={`/market/seller/${userUsername}/`} className={styles.link}>
                            {userUsername}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

ProductES.contextTypes = {
    site: PropTypes.object,
};

export default Styled(defaultStyles)(ProductES);
